<script setup>
import LoadingDummyCustom from '@/assets/js/src/components/loading/LoadingDummyCustom'
import { ERROR, LOADED, LOADING, UPDATING, } from '@/assets/js/src/util/composables/usePrefetchData'
import { useMessagesLoaded, } from '@/assets/js/src/modules/lang/_composables/useMessagesLoaded'
import { useTranslation, } from '@/assets/js/src/util/composables/useTranslation'
import { computed, toRefs, } from 'vue'
import InfiniteScroll from '@/assets/js/src/components/core/InfiniteScroll'
import { useComponentIsActive, } from '@/assets/js/src/util/composables/useComponentIsActive'

const props = defineProps({
    items: {
        type: Object,
        default: () => ({}),
    },
    asyncStatus: {
        type: Number,
        default: LOADING,
    },
    page: {
        type: Number,
        default: 1,
    },
    maxPages: {
        type: Number,
        default: 1,
    },
    noItemsKey: {
        type: String,
        default: '',
    },
    gridView: {
        type: Boolean,
        default: true,
    },
    loadingProps: {
        type: Object,
        default: () => ({
            height:'calc(100% - 16px)',
            margin:'8px',
        }),
    },
})

const emit = defineEmits([ 'load-more', ])
const loadMore = () => {
    emit('load-more')
}
const { componentIsActive, } = useComponentIsActive()
const { asyncStatus,maxPages,noItemsKey, } = toRefs(props)
const { messagesLoaded, } = useMessagesLoaded([ noItemsKey.value.split('.')[1] ?? 'resource', ])
const { t, } = useTranslation()
const noNotice = computed(() =>messagesLoaded.value && ERROR === asyncStatus.value && typeof maxPages.value !== 'undefined')
</script>

<template>
    <transition-group
        v-if="!noNotice"
        name="scale-trans"
        tag="div"
        class="v-row v-row--no-gutters wrap grid-list-content"
        :class="gridView?'grid-list-content__grid':'grid-list-content__list'"
    >
        <v-col
            v-for="(item, index) in items"
            :key="'grid-list-item_' + (item.id || index)"
            :cols="gridView?6:12"
            :sm="gridView?4:undefined"
            :md="gridView?3:6"
            :offset-md="gridView?undefined:3"
            :lg="gridView?2:undefined"
        >
            <slot
                name="item"
                v-bind="{item,index}"
            />
        </v-col>

        <!-- Nachladen Animation bzw. Trigger -->
        <template v-if="page < maxPages && asyncStatus === LOADED">
            <v-col
                v-for="n in 6"
                :key="'lds-'+n"
                class="d-flex justify-center"
                :cols="gridView?6:12"
                :sm="gridView?4:undefined"
                :md="gridView?3:6"
                :offset-md="gridView?undefined:3"
                :lg="gridView?2:undefined"
            >
                <loading-dummy-custom
                    v-bind="loadingProps"
                >
                    <infinite-scroll
                        v-if="n === 1"
                        :handler="loadMore"
                        :should-handle="componentIsActive"
                        :handle-on-mount="false"
                    />
                </loading-dummy-custom>
            </v-col>
        </template>

        <!-- Animation wenn noch nicht geladen bzw. Trigger -->
        <template
            v-if="[UPDATING,LOADING,].includes(asyncStatus) || (asyncStatus === ERROR && typeof maxPages === 'undefined')"
        >
            <v-col
                v-for="n in 6"
                :key="'ld-'+n"
                class="d-flex justify-center"
                :cols="gridView?6:12"
                :sm="gridView?4:undefined"
                :md="gridView?3:6"
                :offset-md="gridView?undefined:3"
                :lg="gridView?2:undefined"
            >
                <loading-dummy-custom
                    v-bind="loadingProps"
                />
            </v-col>
        </template>
    </transition-group>

    <v-row
        v-else
        no-gutters
        data-resultlist
    >
        <v-col
            v-if="items.length === 0"
            key="no-item"
            cols="12"
            md="6"
            offset-md="3"
        >
            <p>
                {{ t(noItemsKey) }}
            </p>
        </v-col>
    </v-row>
</template>

<style lang="scss">
.grid-list-content {
    &__grid {
        margin: 0 -#{map-deep-get($bs-xl, overlay, grid, margin)}!important;
        padding: 0 map-deep-get($bs-xl, body, paddingX);

        @media (max-width: #{map-deep-get($bs-xs, breakpointValue)}px) {
            padding: 0;
        }
    }

    .loading-dummy-item {
        padding-bottom: 83%;
    }

    .scale-trans {
        &-enter-from, &-leave-to {
            transform: scale(0);
            opacity: 0;
        }

        &-move {
            transition: transform .3s;
        }

        &-leave-active {
            position: absolute;
        }
    }
}
</style>
